import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useRenameCustomEmojiMutation } from 'store/services/emoji/emojiApi';
import type { CustomEmoji } from 'types/interfaces/message';
import './RenameEmojiModal.css';

interface RenameEmojiModalProps {
  isOpen: boolean;
  onClose: () => void;
  emoji: CustomEmoji | null;
}

const NAME_REGEX = /^[a-zA-Z0-9_]+$/;

const RenameEmojiModal: React.FC<RenameEmojiModalProps> = ({ isOpen, onClose, emoji }) => {
  const [name, setName] = useState('');
  const [renameCustomEmoji, { isLoading }] = useRenameCustomEmojiMutation();

  useEffect(() => {
    if (isOpen && emoji) {
      setName(emoji.name);
    }
  }, [isOpen, emoji]);

  if (!isOpen || !emoji) return null;

  const slug = name.toLowerCase().replace(/\s+/g, '_');

  const nameErrors: string[] = [];
  if (name.length > 0 && name.length < 2) nameErrors.push('Name must be at least 2 characters');
  if (name.length > 30) nameErrors.push('Name must be 30 characters or fewer');
  if (name.length > 0 && !NAME_REGEX.test(name)) nameErrors.push('Only letters, numbers, and underscores');

  const canSave = name.length >= 2 && name.length <= 30 && NAME_REGEX.test(name) &&
    name !== emoji.name && !isLoading;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      await renameCustomEmoji({ slug: emoji.slug, name }).unwrap();
      toast.success(`Emoji renamed to :${slug}:`);
      onClose();
    } catch (error: any) {
      if (error.status === 409) {
        toast.error('An emoji with that name already exists. Try a different name.');
      } else if (error.status === 403) {
        toast.error('You do not have permission to rename this emoji.');
      } else {
        toast.error(error.data?.message || 'Failed to rename emoji');
      }
    }
  };

  return createPortal(
    <div className="rename-emoji-overlay" onClick={onClose}>
      <div className="rename-emoji-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rename-emoji-header">
          <h3>Rename Emoji</h3>
          <button className="rename-emoji-close" onClick={onClose} type="button">&times;</button>
        </div>

        <div className="rename-emoji-preview">
          <img src={emoji.imageUrl} alt={emoji.name} />
        </div>

        <div className="rename-emoji-field">
          <label className="rename-emoji-label">Emoji Name</label>
          <input
            type="text"
            className="rename-emoji-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={30}
          />
          {name.length > 0 && nameErrors.length === 0 && (
            <div className="rename-emoji-slug-preview">:{slug}:</div>
          )}
          {nameErrors.length > 0 && (
            <div className="rename-emoji-errors">
              {nameErrors.map((err, i) => <div key={i}>{err}</div>)}
            </div>
          )}
        </div>

        <div className="rename-emoji-actions">
          <button className="rename-emoji-cancel" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="rename-emoji-save"
            onClick={handleSave}
            disabled={!canSave}
            type="button"
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RenameEmojiModal;
